import type {
    AudioFingerprintMatch,
    AudioTrackRecording,
} from "@mrflark/spl_sdk";
import type {
    MediaItem,
} from "@mrflark/spl_service_model";
import {
    useQuery,
} from "@tanstack/react-query";
import {
    Music,
} from "lucide-preact";
import type {
    ReactElement,
} from "preact/compat";

import {
    splClient,
} from "../../api/client.ts";

type MusicIdentificationPanelProps = {
    currentItem: MediaItem | null;
    onNavigateToItem?: (item: MediaItem) => void;
};

const formatTimestamp = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remaining = Math.floor(seconds % 60);
    return `${minutes}:${remaining.toString().padStart(2, "0")}`;
};

// groups internal matches by match_media_id so the user sees one row per
// other media item, with the highest-similarity slice as the headline.
const groupInternalMatches = (
    matches: AudioFingerprintMatch[],
): Array<{
    matchMediaId: string;
    bestSimilarity: number;
    sliceCount: number;
    bestSourceStart: number;
    bestMatchStart: number;
}> => {
    const grouped = new Map<string, {
        matchMediaId: string;
        bestSimilarity: number;
        sliceCount: number;
        bestSourceStart: number;
        bestMatchStart: number;
    }>();
    for (const match of matches) {
        const existing = grouped.get(match.match_media_id);
        if (!existing) {
            grouped.set(match.match_media_id, {
                matchMediaId: match.match_media_id,
                bestSimilarity: match.similarity,
                sliceCount: 1,
                bestSourceStart: match.source_start_seconds,
                bestMatchStart: match.match_start_seconds,
            });
            continue;
        }
        existing.sliceCount += 1;
        if (match.similarity > existing.bestSimilarity) {
            existing.bestSimilarity = match.similarity;
            existing.bestSourceStart = match.source_start_seconds;
            existing.bestMatchStart = match.match_start_seconds;
        }
    }
    return Array.from(grouped.values()).sort((a, b) =>
        b.bestSimilarity - a.bestSimilarity
    );
};

// dedupes external recordings by recording_id, keeping the highest-confidence
// hit. AcoustID returns the same recording for adjacent slices of the same
// song, which would otherwise spam the panel.
const dedupeRecordings = (
    recordings: AudioTrackRecording[],
): AudioTrackRecording[] => {
    const byId = new Map<string, AudioTrackRecording>();
    for (const recording of recordings) {
        const existing = byId.get(recording.recording_id);
        if (!existing || recording.confidence > existing.confidence) {
            byId.set(recording.recording_id, recording);
        }
    }
    return Array.from(byId.values()).sort((a, b) =>
        b.confidence - a.confidence
    );
};

export const MusicIdentificationPanel = ({
    currentItem,
    onNavigateToItem,
}: MusicIdentificationPanelProps): ReactElement | null => {
    const isVideo = currentItem?.media_type === "video";

    const {
        data,
    } = useQuery({
        queryKey: ["audioFingerprintMatches", currentItem?.id],
        queryFn: () =>
            splClient.getAudioFingerprintMatches(currentItem?.id ?? ""),
        enabled: isVideo && !!currentItem?.id,
        staleTime: 5 * 60 * 1000,
    });

    if (!isVideo || !data) return null;

    const internalGrouped = groupInternalMatches(data.internal_matches);
    const recordings = dedupeRecordings(data.external_recordings);

    if (internalGrouped.length === 0 && recordings.length === 0) {
        return null;
    }

    return (
        <div className="mt-4 w-full max-w-3xl">
            <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2 flex items-center gap-1">
                <Music size={12} />
                Music identification
            </h3>
            <div className="border border-border rounded-md p-3 bg-bg-secondary space-y-3">
                {recordings.length > 0 && (
                    <div>
                        <div className="text-xs font-semibold text-text-secondary mb-1">
                            External matches (AcoustID)
                        </div>
                        <ul className="space-y-1">
                            {recordings.map((recording) => (
                                <li
                                    key={recording.recording_id}
                                    className="text-sm flex items-baseline gap-2"
                                >
                                    <span className="text-text-primary font-medium">
                                        {recording.title ?? "Untitled"}
                                    </span>
                                    {recording.artist && (
                                        <span className="text-text-secondary">
                                            — {recording.artist}
                                        </span>
                                    )}
                                    <span className="text-xs text-text-secondary ml-auto">
                                        @ {formatTimestamp(
                                            recording.start_seconds,
                                        )} · {(recording.confidence * 100)
                                            .toFixed(0)}% confidence
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {internalGrouped.length > 0 && (
                    <div>
                        <div className="text-xs font-semibold text-text-secondary mb-1">
                            Same music in other videos
                        </div>
                        <ul className="space-y-1">
                            {internalGrouped.map((group) => (
                                <li
                                    key={group.matchMediaId}
                                    className="text-sm flex items-baseline gap-2"
                                >
                                    {onNavigateToItem
                                        ? (
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    try {
                                                        const fetched =
                                                            await splClient
                                                                .getMediaItem(
                                                                    group
                                                                        .matchMediaId,
                                                                );
                                                        onNavigateToItem(
                                                            fetched,
                                                        );
                                                    } catch {
                                                        // navigate-by-id is
                                                        // best-effort; if
                                                        // the matched media
                                                        // was deleted since
                                                        // the match was
                                                        // recorded the user
                                                        // just sees no nav
                                                        // happen
                                                    }
                                                }}
                                                className="text-text-primary font-mono underline hover:text-blue-400"
                                            >
                                                {group.matchMediaId.slice(0, 8)}
                                            </button>
                                        )
                                        : (
                                            <span className="text-text-primary font-mono">
                                                {group.matchMediaId.slice(0, 8)}
                                            </span>
                                        )}
                                    <span className="text-xs text-text-secondary">
                                        {group.sliceCount} slice
                                        {group.sliceCount === 1 ? "" : "s"}{" "}
                                        · best match @ {formatTimestamp(
                                            group.bestSourceStart,
                                        )} ↔{" "}
                                        {formatTimestamp(group.bestMatchStart)}
                                    </span>
                                    <span className="text-xs text-text-secondary ml-auto">
                                        {(group.bestSimilarity * 100).toFixed(
                                            0,
                                        )}% similar
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};
